import React from "react";
import "./DarsSahifa.css";
import { formatSumma } from "../utils/format";
import { useNavigate } from "react-router-dom";
import DarsVideoPlayer from "./DarsVideoPlayer";
import CONFIG from "../config";
import TestModal from "./TestModal";
import { toast } from "react-toastify";
import { FaBookReader, FaInfoCircle, FaArrowLeft, FaChevronLeft, FaChevronRight, FaClipboardCheck, FaLock, FaCheckCircle, FaExpand, FaCompress, FaClock, FaSignal, FaFolderOpen, FaStar, FaExternalLinkAlt, FaDownload, FaCode, FaTrophy } from "react-icons/fa";
import { useVideo } from "../Context/VideoContext";
import ResursModal from "./ResursModal";
import AmaliyModal from "./AmaliyModal";



function DarsSahifa({ kurslar, mavzu, activeMavzu, setActiveMavzu, toggleUserPanel, userPanelOpen, handleLogout, fetchMavzular }) {
    const [lessonData, setLessonData] = React.useState(null);
    const [progressData, setProgressData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [showTest, setShowTest] = React.useState(false);
    const [showResurs, setShowResurs] = React.useState(false);
    const [showAmaliy, setShowAmaliy] = React.useState(false);
    const [showVideo, setShowVideo] = React.useState(true);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const navigate = useNavigate();
    const { floatVideo, videoState, closeFloatingVideo } = useVideo();
    const selectedCourse = kurslar || {};

    // Logic: If lesson loaded, use secure URL. If pending secure, empty string (to trigger loader). 
    // If no lesson (Intro), use course video.
    const currentVideoUrl = lessonData ? lessonData.video_url.replace('/public/', '/') : (selectedCourse.video || "");

    const isFloatingCurrent = videoState.isFloating && videoState.url === currentVideoUrl;
    const topics = Array.isArray(mavzu) ? mavzu : [];
    const currentTopicObj = topics[activeMavzu] || {};
    const currentTopic = currentTopicObj.nomi || "Mavzu nomi";

    const fetchLesson = () => {
        setLoading(true);
        setLessonData(null); // Clear old data to prevent stale text
        const token = localStorage.getItem("loyiha_token");
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

        fetch(`${CONFIG.API_BASE_URL}/get_darslar.php?mavzu_id=${currentTopicObj.id}&token=${token}`, { headers })
            .then(res => res.json())
            .then(data => {
                if (data.status === 'success') {
                    if (data.data.length > 0) {
                        setLessonData(data.data[0]);
                    }
                    setProgressData(data.progress);
                } else {
                    setLessonData(null);
                    if (data.status === 'error' && token) {
                        handleLogout();
                    }
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    React.useEffect(() => {
        if (currentTopicObj.id) {
            fetchLesson();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeMavzu, currentTopicObj.id, selectedCourse.id]);

    // Intersection Observer for Auto-Floating
    const videoSectionRef = React.useRef(null);
    const observerRef = React.useRef(null);

    React.useEffect(() => {
        if (!showVideo || isFloatingCurrent) return;

        const options = {
            root: null,
            rootMargin: '0px',
            threshold: 0.2 // Trigger when only 20% is visible (leaving)
        };

        observerRef.current = new IntersectionObserver((entries) => {
            const [entry] = entries;

            // If checking "not intersecting" - i.e. scrolled out of view
            // AND we want to ensure we don't float if it's just loaded at the bottom, 
            // but usually DarsSahifa video is at top. 
            // We check BoundingClientRect to see if it's ABOVE the viewport (scrolled past).

            if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
                // Player scrolled UP out of view
                // Check if we should float (e.g. if it was playing? 
                // For now, let's just float if it's valid url. 
                // Ideally we only float if playing, but we might not know play state here easily without context.
                // We will float, and let GlobalFloatingPlayer decide autoPlay based on last state or just default to play.

                // Get current time to pass? We relies on localStorage in floatVideo call inside DarsVideoPlayer? 
                // Or better: The floatVideo function in context needs time.

                const savedTime = parseFloat(localStorage.getItem(`video-time-${currentVideoUrl}`) || "0");
                floatVideo(currentVideoUrl, savedTime);
            }
        }, options);

        if (videoSectionRef.current) {
            observerRef.current.observe(videoSectionRef.current);
        }

        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [showVideo, isFloatingCurrent, currentVideoUrl, floatVideo]);

    // Handle Restore (Scroll back to video if docked)
    const prevIsFloating = React.useRef(isFloatingCurrent);
    React.useEffect(() => {
        // If it WAS floating, and now is NOT (meaning we docked/restored)
        // AND appropriate to scroll 
        if (prevIsFloating.current && !isFloatingCurrent && videoSectionRef.current) {
            // Scroll smoothly to the video
            videoSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        prevIsFloating.current = isFloatingCurrent;
    }, [isFloatingCurrent]);


    // Calculate max unlocked index
    // Find the first topic that is NOT passed. That is the "current active" one.
    // Anything after that is locked.
    // Example: 0 (Passed), 1 (Passed), 2 (Not Passed). Max unlocked is 2. 3 is Locked.
    // Logic: Users can access any passed topic + the first unpassed one.

    const maxUnlockedIndex = topics.length > 0 ? topics.findIndex(t => !t.is_passed) : -1;
    const effectiveLimit = maxUnlockedIndex === -1 ? topics.length - 1 : maxUnlockedIndex;

    const isPassed = progressData?.test_status?.ball >= 4;

    const handleTopicClick = (index) => {
        if (!topics[index]) return;
        if (index <= effectiveLimit || topics[index].is_passed) {
            setActiveMavzu(index);
        } else {
            toast.error("Ushbu mavzu hali ochilmagan! Avvalgi darslarni yakunlang.");
        }
    };

    const getCourseLang = () => {
        const cName = selectedCourse?.nomi?.toLowerCase() || "";
        if (cName.includes("javascript") || cName.includes("js")) return "js";
        if (cName.includes("php")) return "php";
        if (cName.includes("python")) return "python";
        if (cName.includes("css")) return "css";
        return "html";
    };

    // Check if course is purchased
    const isPurchased = selectedCourse.id && (Number(selectedCourse.sotib_olingan) === 1);

    if (selectedCourse.id && !isPurchased) {
        return (
            <div className="dars-sahifa-container" style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                <div style={{ textAlign: 'center', padding: '40px', maxWidth: '600px', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', borderRadius: '20px', border: '1px solid rgba(139, 71, 21, 0.3)', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
                    <FaLock style={{ fontSize: '80px', color: '#e67e22', marginBottom: '20px' }} />
                    <h2 style={{ fontSize: '2rem', marginBottom: '15px', color: '#333' }}>Kurs yopilgan</h2>
                    <p style={{ fontSize: '1.2rem', marginBottom: '30px', color: '#666', lineHeight: '1.6' }}>
                        Ushbu darslikni ko'rish uchun kursni sotib olishingiz kerak.
                    </p>
                    <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
                        <button
                            onClick={() => navigate(-1)}
                            style={{
                                padding: '12px 30px',
                                border: '2px solid #e67e22',
                                background: 'transparent',
                                color: '#e67e22',
                                borderRadius: '30px',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}
                        >
                            <FaArrowLeft /> Ortga qaytish
                        </button>
                        <button
                            onClick={() => navigate('/bosh')}
                            style={{
                                padding: '12px 30px',
                                background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
                                border: 'none',
                                color: 'white',
                                borderRadius: '30px',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                boxShadow: '0 10px 20px rgba(230, 126, 34, 0.4)'
                            }}
                        >
                            Kurslarni ko'rish
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dars-sahifa-container">
            {/* 30% Syllabus Side Panel */}
            <aside className="dars-syllabus">
                <div className="syllabus-header">
                    <button className="back-button" onClick={() => navigate(-1)}>
                        <FaArrowLeft />
                    </button>
                    <h3>Kurs Mavzulari</h3>
                </div>

                <ul className="syllabus-list">
                    {topics.map((item, index) => {
                        const isLocked = index > effectiveLimit;
                        return (
                            <li
                                key={index}
                                className={`${activeMavzu === index ? "active-topic" : ""} ${isLocked ? "topic-locked" : ""}`}
                                onClick={() => handleTopicClick(index)}
                            >
                                <span className="topic-num">{index + 1}.</span>
                                <span className="topic-name">{item.nomi}</span>
                                {isLocked ? <FaLock className="lock-icon" /> : (item.is_passed && <FaCheckCircle className="check-icon" style={{ marginLeft: 'auto', color: '#2ecc71' }} />)}
                            </li>
                        );
                    })}
                </ul>
            </aside>

            {/* 70% Learning Content Area */}
            <main className="dars-content">
                {(showVideo && !isFloatingCurrent) && (
                    <div className="video-player-section">
                        {loading ? (
                            <div className="loader-container">Video 'load' bo'lmoqda...</div>
                        ) : (
                            <div className="video-wrapper-relative" ref={videoSectionRef}>
                                <DarsVideoPlayer
                                    src={currentVideoUrl}
                                    onPlay={() => setIsPlaying(true)}
                                    onPause={() => setIsPlaying(false)}
                                    mavzuId={currentTopicObj.id}
                                    videoId={lessonData?.id}
                                />

                                <div
                                    className="test-trigger-panel"
                                    style={{
                                        opacity: isPlaying ? 0 : 1,
                                        pointerEvents: isPlaying ? 'none' : 'auto',
                                        transition: 'opacity 0.3s ease'
                                    }}
                                >
                                    {lessonData?.amaliy_topshiriq && (
                                        <button
                                            className={`start-amaliy-btn ${lessonData.amaliy_status ? 'amaliy-completed' : ''}`}
                                            onClick={() => setShowAmaliy(true)}
                                        >
                                            <FaCode /> Amaliy Topshiriq
                                            {lessonData.amaliy_status && (
                                                <span className="amaliy-tooltip-v2">
                                                    Bajarilgan ({lessonData.amaliy_status.urinishlar_soni}-urinish)
                                                </span>
                                            )}
                                        </button>
                                    )}

                                    {lessonData?.resurslar_list && lessonData.resurslar_list.length > 0 && (
                                        <button
                                            onClick={() => setShowResurs(true)}
                                            className="start-amaliy-btn" // Reuse existing class for consistent style or keep custom
                                            style={{
                                                background: '#2ecc71', // Keep green distinct color
                                                // Inherit other styles from class if possible, or override
                                                marginRight: '10px'
                                            }}
                                        >
                                            <FaFolderOpen /> Resurslar
                                        </button>
                                    )}

                                    {isPassed ? (
                                        <div className="test-passed-badge">
                                            <FaCheckCircle /> Test topshirilgan
                                        </div>
                                    ) : (
                                        <button className="start-test-btn" onClick={() => setShowTest(true)}>
                                            <FaClipboardCheck /> Test topshirish
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="topic-info-card">
                    <div className="info-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', gap: '10px' }}>
                        <div className="info-details">
                            <span className="info-item">
                                <FaClock style={{ color: '#8b4715' }} />
                                <strong>Davomiyligi:</strong> {lessonData?.davomiyligi || "15"} daqiqa
                            </span>
                            <span className="info-item">
                                <FaSignal style={{ color: '#8b4715' }} />
                                <strong>Daraja:</strong> {lessonData?.daraja || "Boshlang'ich"}
                            </span>
                            <span className="info-item">
                                <FaStar style={{ color: '#f1c40f' }} />
                                <strong>Baho:</strong> {progressData?.test_status?.ball ? `${parseInt(progressData.test_status.ball)}/5` : "Topshirilmagan"}
                            </span>
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                className="video-toggle-btn-icon"
                                onClick={() => {
                                    if (isFloatingCurrent) {
                                        closeFloatingVideo();
                                    } else {
                                        const savedTime = parseFloat(localStorage.getItem(`video-time-${currentVideoUrl}`) || "0");
                                        floatVideo(currentVideoUrl, savedTime);
                                    }
                                }}
                                title={isFloatingCurrent ? "Videoni katta qilish" : "Videoni kichik (suzuvchi) qilish"}
                                style={{
                                    background: '#8b4715',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 10px rgba(139, 71, 21, 0.3)',
                                    transition: 'all 0.3s ease',
                                    fontSize: '1.2rem',
                                    flexShrink: 0
                                }}
                            >
                                {isFloatingCurrent ? <FaExpand /> : <FaCompress />}
                            </button>
                        </div>
                    </div>

                    <div className="info-description" style={{ marginTop: '0' }}>
                        {lessonData?.theory && lessonData.theory.length > 0 ? (
                            <div dangerouslySetInnerHTML={{ __html: lessonData.theory[0].matn }} />
                        ) : (
                            <p>
                                Mavzu haqida nazariy ma'lumotlar tez kunda qo'shiladi.
                                Hozircha video darslikni ko'rib mavzuni o'rganishingiz mumkin.
                            </p>
                        )}
                    </div>
                </div>
            </main>

            {/* Sidebar Toggle Shortcut */}
            <button
                className={`sidebar-toggle-btn ${userPanelOpen ? 'side-open' : ''}`}
                onClick={toggleUserPanel}
                title={userPanelOpen ? "Profilni yopish" : "Profilni ko'rish"}
            >
                {userPanelOpen ? <FaChevronRight /> : <FaChevronLeft />}
            </button>

            {/* Test Modal */}
            {showTest && (
                <TestModal
                    mavzuId={currentTopicObj.id}
                    mavzuNomi={currentTopic}
                    onClose={() => setShowTest(false)}
                    onPass={() => {
                        setShowTest(false);
                        // 1. Refresh global mavzular list to update 'is_passed' stickers and unlock next link
                        if (fetchMavzular && selectedCourse.id) {
                            fetchMavzular(selectedCourse.id, true);
                        }

                        // 2. Auto-advance logic
                        if (activeMavzu < topics.length - 1) {
                            setActiveMavzu(prev => prev + 1);
                            toast.success("Tabriklaymiz! Keyingi mavzuga o'tildi.");
                        } else {
                            toast.success("Kurs muvaffaqiyatli yakunlandi!");
                            fetchLesson(); // Just refresh if it's the last one
                        }
                    }}
                />
            )}

            {/* Resurs Modal */}
            {showResurs && (
                <ResursModal
                    resources={lessonData?.resurslar_list}
                    onClose={() => setShowResurs(false)}
                />
            )}

            {/* Amaliy Modal */}
            {showAmaliy && (
                <AmaliyModal
                    mavzuId={currentTopicObj.id}
                    taskData={lessonData?.amaliy_topshiriq}
                    history={lessonData?.amaliy_history}
                    lang={getCourseLang()}
                    onClose={() => setShowAmaliy(false)}
                    onPass={() => {
                        setShowAmaliy(false);
                        toast.success("Amaliy topshiriq qabul qilindi!");
                        fetchLesson();
                    }}
                />
            )}
        </div>
    );
}

export default DarsSahifa;
